import React, { useEffect, useState } from 'react';
import RacingBars from 'racing-bars/react';
import './RaceBar.css';
import { getRacingLogs } from '../../../firebase-functions/database';
import { BeanHead } from 'beanheads';
import { renderToStaticMarkup } from 'react-dom/server';
import dayjs from 'dayjs';

// --------------------------------
// Utilities
// --------------------------------
export const dataTransform = (data, players) => {
  // Cache avatar SVGs per unique player
  const svgCache = {};

  Object.entries(players).forEach(([seed, player]) => {
    if (!player?.avatar) return;

    const avatar = { ...player.avatar };
    if (avatar.faceMask === 'false' || avatar.faceMask === false) {
      delete avatar.faceMaskColor;
      avatar.faceMask = false;
    }

    const beanSvg = renderToStaticMarkup(<BeanHead {...avatar} />);
    const svgDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(beanSvg)}`;
    svgCache[seed] = svgDataUrl;
  });

  return data.map((d) => ({
    ...d,
    icon: svgCache[d.avatarSeed] || 'https://flagsapi.com/US/flat/64.png',
  }));
};

const convertDateToYYYYMMDD = (date) => {
  const t = new Date(date);
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// --------------------------------
// Date Range Hook
// --------------------------------
const optionsDayWeekMonth = ['Week', 'Month', 'All Time'];
const optionsWhich = ['This', 'Last', '2 ago', '3 ago', '4 ago'];
const optionsFilter = ['Everyone', 'My League', 'My Room', 'Reading Group'];

function useDateRange() {
  const [timeframe, setTimeframe] = useState('Month');
  const [timeframeWhich, setTimeframeWhich] = useState('This');

  const getDatesFromOptions = () => {
    const today = dayjs();
    let start, end;

    if (timeframe === 'All Time') {
      start = dayjs('2020-01-01');
      end = today;
    } else if (timeframe === 'Week') {
      const weeksAgo = optionsWhich.indexOf(timeframeWhich);
      // Calculate start first
      start = today.subtract(weeksAgo, 'week').startOf('week');
      // Set end relative to THAT start
      end = start.endOf('week');
    } else if (timeframe === 'Month') {
      const monthsAgo = optionsWhich.indexOf(timeframeWhich);
      // Calculate start first (e.g., Jan 1st)
      start = today.subtract(monthsAgo, 'month').startOf('month');
      // Calculate end relative to Jan 1st (e.g., Jan 31st)
      end = start.endOf('month');
    }

    const days = [];
    let current = start;
    // Note: ensure 'day' granularity for the comparison
    while (current.isBefore(end, 'day') || current.isSame(end, 'day')) {
      days.push(current.format('YYYY-MM-DD'));
      current = current.add(1, 'day');
    }

    return days;
  };
  return {
    timeframe,
    setTimeframe,
    timeframeWhich,
    setTimeframeWhich,
    getDatesFromOptions,
  };
}

// --------------------------------
// Data Processing
// --------------------------------
const filterLogsByDateRange = (logs, getDatesFromOptions) => {
  return logs
    .map((entry) => ({
      avatarSeed: entry.avatarSeed,
      date: convertDateToYYYYMMDD(entry.timestamp.toDate()),
      name: entry.name,
      value: entry.gainedXP,
      league: entry.league,
      readingGroup: entry.readingGroup,
    }))
    .filter((entry) => getDatesFromOptions().includes(entry.date));
};
const sumLogsByDateAndAvatar = (logs) => {
  const groups = new Map();

  logs.forEach((entry) => {
    // Create a unique key for the combination
    const key = `${entry.date}-${entry.avatarSeed}`;

    if (groups.has(key)) {
      // If we've seen this key, just update the value
      groups.get(key).value += entry.value;
    } else {
      // If it's new, store a copy in the Map
      groups.set(key, { ...entry });
    }
  });

  // Convert the Map values back into an array
  return Array.from(groups.values());
};

const accumulateLogsOverTime = (logs) => {
  const previousScores = new Map();
  const len = logs.length;

  for (let i = 0; i < len; i++) {
    const entry = logs[i];
    const seed = entry.avatarSeed;

    // Calculate new total
    const total = Math.floor((previousScores.get(seed) || 0) + entry.value);

    // Update the Map and the object
    previousScores.set(seed, total);
    entry.value = total;
  }
  return logs;
};

const populateMissingDates = (accumulatingLogs) => {
  if (accumulatingLogs.length === 0) return [];
  const studentIds = [...new Set(accumulatingLogs.map((e) => e.avatarSeed))];
  const start = new Date(accumulatingLogs[0].date);
  const end = new Date(accumulatingLogs[accumulatingLogs.length - 1].date);

  const days = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    days.push(convertDateToYYYYMMDD(new Date(d)));
  }

  const populated = [];
  const previousValues = {};

  days.forEach((day) => {
    studentIds.forEach((id) => {
      const existing = accumulatingLogs.find(
        (e) => e.avatarSeed === id && e.date === day,
      );
      if (existing) {
        populated.push(existing);
        previousValues[id] = existing;
      } else if (previousValues[id]) {
        populated.push({ ...previousValues[id], date: day });
      } else {
        const racer = accumulatingLogs.find((e) => e.avatarSeed === id);
        if (racer)
          populated.push({
            avatarSeed: id,
            name: racer.name,
            date: day,
            value: 0,
          });
        previousValues[id] = { ...racer, value: 0 };
      }
    });
  });

  return populated;
};

// --------------------------------
// New: Filter by league, room, readingGroup
// --------------------------------
const applyFilter = (data, players, filterMode, league, room, readingGroup) => {
  if (filterMode === 'Everyone') return data;

  return data.filter((entry) => {
    const player = players[entry.avatarSeed];

    // if (!player) return false;

    switch (filterMode) {
      case 'My League':
        return entry.league && entry.league === league;
      case 'My Room': {
        return player?.room && player?.room === room;
      }
      case 'Reading Group':
        return entry.readingGroup && entry.readingGroup === true;
      default:
        return true;
    }
  });
};

const sumLogsByDateAndAvatarOptimized = (logs) => {
  const groups = new Map();
  for (const entry of logs) {
    const key = `${entry.date}_${entry.avatarSeed}`;
    if (groups.has(key)) {
      groups.get(key).value += entry.value;
    } else {
      groups.set(key, { ...entry });
    }
  }
  return Array.from(groups.values());
};

const populateMissingDatesOptimized = (accumulatingLogs) => {
  if (accumulatingLogs.length === 0) return [];

  const logLookup = new Map();
  const studentMeta = new Map();

  // Single pass to map existing data
  accumulatingLogs.forEach((e) => {
    logLookup.set(`${e.avatarSeed}_${e.date}`, e);
    if (!studentMeta.has(e.avatarSeed)) {
      studentMeta.set(e.avatarSeed, { name: e.name, avatarSeed: e.avatarSeed });
    }
  });

  const start = new Date(accumulatingLogs[0].date);
  const end = new Date(accumulatingLogs[accumulatingLogs.length - 1].date);
  const studentIds = Array.from(studentMeta.keys());

  const populated = [];
  const lastKnownValues = new Map();

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const currentDay = d.toISOString().split('T')[0]; // Quick YYYY-MM-DD

    studentIds.forEach((id) => {
      const existing = logLookup.get(`${id}_${currentDay}`);
      if (existing) {
        populated.push(existing);
        lastKnownValues.set(id, existing);
      } else {
        const lastVal = lastKnownValues.get(id) || {
          ...studentMeta.get(id),
          value: 0,
        };
        populated.push({ ...lastVal, date: currentDay });
      }
    });
  }
  return populated;
};

const accumulateLogsOverTimeOptimized = (logs) => {
  const previousScores = new Map();
  // We modify in-place or map to avoid heavy object spreading if possible
  return logs.map((entry) => {
    const seed = entry.avatarSeed;
    const total = (previousScores.get(seed) || 0) + entry.value;
    const roundedTotal = Math.floor(total);
    previousScores.set(seed, roundedTotal);
    return { ...entry, value: roundedTotal };
  });
};

// --------------------------------
// Data Loader
// --------------------------------
const loadAndTransformData = async (
  getDatesFromOptions,
  setData,
  setAvatars,
  setError,
  filterMode,
  league,
  room,
  readingGroup,
  setLastUpdateDate,
) => {
  try {
    const res = await getRacingLogs();
    if (!res?.success) throw new Error('Failed to load racing logs');

    const logs = res.message.entries || [];
    const players = res.message.players || {};

    if (logs.length === 0) {
      setData([]);
      return;
    }

    // 1. Immediate UI Updates
    setAvatars(players);
    setLastUpdateDate(res.message.updatedAt);

    // 2. STAGE 1: Narrow the dataset as early as possible (O(n))
    const dateFiltered = filterLogsByDateRange(logs, getDatesFromOptions);

    // Move applyFilter here! Processing 5-10 players is much faster than 100+.
    const contextFiltered = applyFilter(
      dateFiltered,
      players,
      filterMode,
      league,
      room,
      readingGroup,
    );

    // 3. STAGE 2: Aggregate (Using Map for O(n) speed)
    const summed = sumLogsByDateAndAvatarOptimized(contextFiltered);

    // console.log({ summed });

    // 4. STAGE 3: Accumulate Running Totals (O(n))
    const accumulated = accumulateLogsOverTimeOptimized(summed);
    // console.log({ accumulated });

    // 5. STAGE 4: Fill Gaps (Now only for the filtered players)
    const populated = populateMissingDatesOptimized(accumulated);

    setData(populated);
  } catch (err) {
    console.error('Data Transformation Error:', err);
    setError(err.message);
  }
};

// --------------------------------
// Main Component
// --------------------------------
export const RaceBar = (props) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [avatars, setAvatars] = useState({});
  const [filterMode, setFilterMode] = useState('Everyone');
  const [lastUpdatedDate, setLastUpdateDate] = useState(null);
  const handleUpdateReadingRaceLogs = props.handleUpdateReadingRaceLogs;

  const {
    timeframe,
    setTimeframe,
    timeframeWhich,
    setTimeframeWhich,
    getDatesFromOptions,
  } = useDateRange();

  useEffect(() => {
    if (!props.studentProfile || !props.readerProfile) return;

    setData(null); // reset
    loadAndTransformData(
      getDatesFromOptions,
      setData,
      setAvatars,
      setError,
      filterMode,
      props.readerProfile.league,
      props.studentProfile.pet,
      props.readerProfile.collab,
      setLastUpdateDate,
    );
  }, [timeframe, timeframeWhich, filterMode]);

  if (error) return <h2 style={{ color: 'red' }}>{error}</h2>;
  if (!data) return <h2>Loading...</h2>;
  // if (data.length === 0) return <p>No Data Available...</p>;

  const categoryFields = Array.from(new Set(data.map((d) => d.name)));
  const labels = categoryFields.map((name) => ({
    label: name,
    icon:
      data.find((d) => d.name === name)?.icon ||
      'https://flagsapi.com/US/flat/64.png',
  }));

  const transformedData = dataTransform(data, avatars);

  const formatLastUpdated = (dateInput) => {
    const now = new Date();
    const past = new Date(dateInput);
    const diffInMs = now - past;

    // Define time constants in milliseconds
    const second = 1000;
    const minute = second * 60;
    const hour = minute * 60;
    const day = hour * 24;
    const month = day * 30; // Approximation
    const year = day * 365;

    if (diffInMs < minute) {
      const seconds = Math.floor(diffInMs / second);
      return ` ${seconds} seconds ago`;
    }

    const units = [
      { label: 'year', value: year },
      { label: 'month', value: month },
      { label: 'day', value: day },
      { label: 'hour', value: hour },
      { label: 'minute', value: minute },
    ];

    for (let i = 0; i < units.length; i++) {
      const unit = units[i];
      if (diffInMs >= unit.value) {
        const primaryValue = Math.floor(diffInMs / unit.value);
        const remainingMs = diffInMs % unit.value;

        // Get the next smaller unit for the "b" part of the string
        const nextUnit = units[i + 1];

        if (nextUnit && remainingMs >= nextUnit.value) {
          const secondaryValue = Math.floor(remainingMs / nextUnit.value);
          return `${primaryValue} ${unit.label}${primaryValue !== 1 ? 's' : ''} and ${secondaryValue} ${nextUnit.label}${secondaryValue !== 1 ? 's' : ''} ago`;
        }

        // Fallback if the remainder is too small for the next unit
        return `${primaryValue} ${unit.label}${primaryValue !== 1 ? 's' : ''} ago`;
      }
    }
  };

  return (
    <div className='racebar-container'>
      <h2>🏁 Reading Race Leaderboard (words x percentage)</h2>

      <p
        style={{
          color: 'white',
        }}>
        Last updated {formatLastUpdated(lastUpdatedDate.toDate())}
        {(new Date() - new Date(lastUpdatedDate.toDate())) / 60000 > 5 && (
          <button
            className='btn-update-racing-logs'
            onClick={handleUpdateReadingRaceLogs}>
            Update
          </button>
        )}
      </p>

      {/* Controls */}
      <div className='racebar-controls' style={{ marginBottom: '1rem' }}>
        <label>
          <select
            value={timeframe}
            onChange={(e) => {
              setTimeframe(e.target.value);
              setTimeframeWhich('This');
            }}>
            {optionsDayWeekMonth.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>

        {timeframe !== 'All Time' && (
          <label>
            <select
              value={timeframeWhich}
              onChange={(e) => setTimeframeWhich(e.target.value)}>
              {optionsWhich.map((opt) => (
                <option key={opt} value={opt}>
                  {opt.split('ago').length === 2
                    ? opt.split('ago')[0] + timeframe + 's Ago'
                    : opt.split('ago')[0] + ' ' + timeframe}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}>
            {optionsFilter.map((opt) => (
              <option key={opt}>{opt}</option>
            ))}
          </select>
        </label>
      </div>

      <RacingBars
        className='race'
        data={transformedData}
        options={{
          theme: 'dark',
          colorMap: [
            '#636EFA',
            '#EF553B',
            '#00CC96',
            '#AB63FA',
            '#FFA15A',
            '#19D3F3',
            '#FF6692',
            '#B6E880',
            '#FF97FF',
            '#FECB52',
            '#FF6692',
            '#B6E880',
            '#FF97FF',
            '#FECB52',
          ],
          showGroups: false,
        }}
        autorun
        runOnce={false}
        duration={100}
        dateCounter='DD-MM-YYYY'
        nameField='name'
        valueField='value'
        dateLabel='Step'
        labels={labels}
        showIcons>
        Loading Race...
      </RacingBars>
    </div>
  );
};
